/**
 * A People Leader gives feedback on a team member directly (HR UAT #11, Oct 2026).
 *
 * Until now a People Leader could only request feedback about a team member.
 * There is no separate store for their own: the dialog records a People
 * Leader request about the team member with the leader as its only
 * stakeholder, then answers it through POST /feedback/responses with the
 * usual response form. It sits with the leader's other feedback for that
 * person (People Leader-Initiated), and the leader can add it to the team
 * member's Impact Portfolio from its details like any other. Nobody is
 * emailed: the service sends no invitation to a request's own requester.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import FeedbackResponseForm, { QuestionResponse } from './FeedbackResponseForm'
import { defaultQuestions, snapshotFromTemplate } from './questionSnapshot'
import {
  feedbackService,
  type FeedbackRequest,
  type FeedbackStakeholder,
} from '@/services/api/feedbackService'
import questionBankService, { type FeedbackTemplate } from '@/services/api/questionBankService'
import { useAuth } from '@/providers/AuthProvider'

/** Title of the request that holds a People Leader's own feedback. */
export const GIVE_FEEDBACK_TITLE = 'People Leader feedback'

interface GiveFeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The team member the feedback is about (employee id). */
  subjectId: string
  subjectName: string
  onGiven?: () => void
}

export default function GiveFeedbackDialog({
  open,
  onOpenChange,
  subjectId,
  subjectName,
  onGiven,
}: GiveFeedbackDialogProps) {
  const { user } = useAuth()
  const [templates, setTemplates] = useState<FeedbackTemplate[]>([])
  const [template, setTemplate] = useState<FeedbackTemplate | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // The request already recorded for these questions, so answers that failed
  // to save can be sent again without recording a second request.
  const recorded = useRef<{ key: string; request: FeedbackRequest } | null>(null)

  // The same templates a People Leader's feedback request starts from
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    questionBankService
      .listTemplates({ feedback_context: 'matrix', is_active: true, limit: 50 })
      .then((response) => {
        if (cancelled) return
        setTemplates(response.items)
        setTemplate(response.items.find((t) => t.is_default) || response.items[0] || null)
      })
      .catch((error) => {
        console.error('Failed to load feedback templates:', error)
        if (cancelled) return
        setTemplates([])
        setTemplate(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const questionsKey = template?.id || 'default'
  const questions = useMemo(() => {
    const fromTemplate = template ? snapshotFromTemplate(template) : []
    return fromTemplate.length > 0 ? fromTemplate : defaultQuestions('MATRIX')
  }, [template])

  const handleSubmit = async (answers: QuestionResponse[]) => {
    const leaderId = user?.employeeId
    if (!leaderId) {
      toast.error('We could not confirm who you are. Please sign in again.')
      throw new Error('The signed-in user has no employee id')
    }
    setSubmitting(true)
    try {
      let request = recorded.current?.key === questionsKey ? recorded.current.request : null
      if (!request) {
        const leader = {
          stakeholder_user_id: leaderId,
          stakeholder_name: user?.displayName || user?.email || 'People Leader',
          stakeholder_email: user?.email || undefined,
          category: 'other' as const,
        }
        request = await feedbackService.createFeedbackRequest({
          request_type: 'matrix',
          requested_for_user_id: subjectId,
          requested_by_user_id: leaderId,
          title: GIVE_FEEDBACK_TITLE,
          question_template_id: template?.id,
          custom_questions: questions,
          // A stakeholder to create has no request id yet
          stakeholders: [leader as FeedbackStakeholder],
          is_confidential: false,
          is_anonymous: false,
          ai_analysis_enabled: false,
          integrate_to_evidence: true,
        })
        recorded.current = { key: questionsKey, request }
      }
      const mine = request.stakeholders?.find((s) => s.stakeholder_user_id === leaderId)
      if (!mine?.id) throw new Error('The recorded request has no place for your answers')
      await feedbackService.submitFeedbackResponse({
        feedback_request_id: request.id!,
        feedback_stakeholder_id: mine.id,
        is_anonymous: false,
        response_data: answers,
      })
      recorded.current = null
      toast.success(`Your feedback on ${subjectName} is saved`)
      onOpenChange(false)
      onGiven?.()
    } catch (error) {
      console.error('Failed to give feedback:', error)
      const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'Your feedback was not saved. Please try again.')
      // Rethrow so the form keeps the answers for another try
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* As for answering a request: a click outside must not lose the answers. */}
      <DialogContent
        className="max-w-[940px] w-[94vw] max-h-[92vh] overflow-y-auto p-0 sm:rounded-lg"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Give feedback on {subjectName}</DialogTitle>
          <DialogDescription>
            Your feedback is saved with your other feedback for {subjectName}.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-heritage-blue" />
          </div>
        ) : (
          <>
            <div className="space-y-3 bg-[#FAFAF7] px-6 pt-8 sm:px-12">
              <p className="text-sm text-slate-600">
                It is saved under People Leader-Initiated with your other feedback for {subjectName}.
                To share it with them, add it to their Impact Portfolio from its details.
              </p>
              {templates.length > 1 && (
                <div className="max-w-sm space-y-1">
                  <Label htmlFor="give-feedback-template" className="text-xs text-slate-500">
                    Questions
                  </Label>
                  <Select
                    value={template?.id || ''}
                    onValueChange={(id) => setTemplate(templates.find((t) => t.id === id) || null)}
                  >
                    <SelectTrigger id="give-feedback-template">
                      <SelectValue placeholder="Choose a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.template_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {/* Keyed by the questions: another template starts a fresh form
                (and draft), so answers to questions no longer shown are not sent. */}
            <FeedbackResponseForm
              key={questionsKey}
              requestId={`give-${subjectId}-${questionsKey}`}
              requestTitle="Give feedback"
              requestedForUser={subjectName}
              questions={questions}
              kicker={GIVE_FEEDBACK_TITLE}
              onSubmit={handleSubmit}
              onCancel={() => onOpenChange(false)}
              loading={submitting}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
